import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { of, throwError, delay } from 'rxjs';
import { User, UserRole } from '../core/models/user.model';
import { ListUsersResponse } from '../core/services/users.service';

// Mock users database
const mockUsers: User[] = [
  {
    id: '1',
    name: 'Admin User',
    email: 'admin@example.com',
    phone: '+34600111222',
    company: 'Admin Corp',
    roles: ['admin'],
    created_at: '2024-01-15T10:30:00Z',
    updated_at: '2024-01-15T10:30:00Z',
    active: true,
  },
  {
    id: '2',
    name: 'Partner User',
    email: 'partner@example.com',
    phone: '+34600333444',
    company: 'Partner Solutions SL',
    sdi_contact: {
      id: 'sdi-1',
      name: 'SDi Contact Person',
      email: 'sdi@partner.com',
      phone: '+34600555666',
    },
    roles: ['partner'],
    created_at: '2024-02-20T14:15:00Z',
    updated_at: '2024-02-20T14:15:00Z',
    active: true,
  },
  {
    id: '3',
    name: 'Another Partner',
    email: 'another@partner.com',
    phone: '+34600777888',
    company: 'Another Partners Ltd',
    roles: ['partner'],
    created_at: '2024-03-10T09:00:00Z',
    updated_at: '2024-03-10T09:00:00Z',
    active: false,
  },
];

/**
 * Mock User Handler
 * Intercepta las peticiones a /api/users/* y devuelve respuestas simuladas
 */
export const userMockHandler: HttpInterceptorFn = (req, next) => {
  const { url, method, body } = req;

  // GET /api/users - List users
  if (url.match(/\/api\/users(\?.*)?$/) && method === 'GET') {
    const params = new URL(url, 'http://dummy').searchParams;
    return handleListUsers(params);
  }

  // GET /api/users/:id - Get user by ID
  if (url.match(/\/api\/users\/[^/]+$/) && method === 'GET') {
    const parts = url.split('/');
    const id = parts[parts.length - 1] ?? '';
    return handleGetUser(id);
  }

  // POST /api/users - Create user
  if (url.endsWith('/api/users') && method === 'POST') {
    return handleCreateUser(body as Partial<User>);
  }

  // PATCH /api/users/:id - Update user
  if (url.match(/\/api\/users\/[^/]+$/) && method === 'PATCH') {
    const parts = url.split('/');
    const id = parts[parts.length - 1] ?? '';
    return handleUpdateUser(id, body as Partial<User>);
  }

  // DELETE /api/users/:id - Delete user
  if (url.match(/\/api\/users\/[^/]+$/) && method === 'DELETE') {
    const parts = url.split('/');
    const id = parts[parts.length - 1] ?? '';
    return handleDeleteUser(id);
  }

  // Si no es una ruta de users, continuar con la petición normal
  return next(req);
};

function handleListUsers(params: URLSearchParams) {
  const page = Number(params.get('page')) || 1;
  const limit = Number(params.get('limit')) || 10;
  const role = params.get('role');
  const search = params.get('search')?.toLowerCase();

  let filteredUsers = [...mockUsers];

  // Filtrar por rol
  if (role) {
    filteredUsers = filteredUsers.filter((user) => user.roles.includes(role as UserRole));
  }

  // Filtrar por búsqueda (nombre, email, company)
  if (search) {
    filteredUsers = filteredUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search) ||
        user.company?.toLowerCase().includes(search)
    );
  }

  const total = filteredUsers.length;
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginatedUsers = filteredUsers.slice(start, end);

  const response: ListUsersResponse = {
    data: paginatedUsers,
    links: {
      first: `/api/users?page=1&limit=${limit}`,
      last: `/api/users?page=${Math.ceil(total / limit)}&limit=${limit}`,
      prev: page > 1 ? `/api/users?page=${page - 1}&limit=${limit}` : null,
      next: end < total ? `/api/users?page=${page + 1}&limit=${limit}` : null,
    },
    meta: {
      total: total,
      per_page: limit,
      current_page: page,
      last_page: Math.ceil(total / limit),
    },
  };

  return of(
    new HttpResponse({
      status: 200,
      body: response,
    })
  ).pipe(delay(300));
}

function handleGetUser(id: string) {
  const user = mockUsers.find((u) => u.id === id);

  if (!user) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 404,
          statusText: 'Not Found',
          error: { message: 'User not found.' },
          url: `/api/users/${id}`,
        })
    ).pipe(delay(200));
  }

  return of(
    new HttpResponse({
      status: 200,
      body: { data: user },
    })
  ).pipe(delay(300));
}

function handleCreateUser(data: Partial<User>) {
  if (!data.name || !data.email) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Name and email are required.' },
          url: '/api/users',
        })
    ).pipe(delay(200));
  }

  const newUser: User = {
    id: crypto?.randomUUID?.() ?? String(Date.now()),
    name: data.name,
    email: data.email,
    phone: data.phone,
    company: data.company,
    sdi_contact: data.sdi_contact,
    roles: data.roles || ['partner'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    active: true,
  };

  mockUsers.push(newUser);

  return of(
    new HttpResponse({
      status: 201,
      body: { data: newUser },
    })
  ).pipe(delay(400));
}

function handleUpdateUser(id: string, data: Partial<User>) {
  const userIndex = mockUsers.findIndex((u) => u.id === id);

  if (userIndex === -1) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 404,
          statusText: 'Not Found',
          error: { message: 'User not found.' },
          url: `/api/users/${id}`,
        })
    ).pipe(delay(200));
  }

  const updatedUser: User = {
    ...mockUsers[userIndex],
    ...data,
    id,
    updated_at: new Date().toISOString(),
  };

  mockUsers[userIndex] = updatedUser;

  return of(
    new HttpResponse({
      status: 200,
      body: { data: updatedUser },
    })
  ).pipe(delay(400));
}

function handleDeleteUser(id: string) {
  const userIndex = mockUsers.findIndex((u) => u.id === id);

  if (userIndex === -1) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 404,
          statusText: 'Not Found',
          error: { message: 'User not found.' },
          url: `/api/users/${id}`,
        })
    ).pipe(delay(200));
  }

  mockUsers.splice(userIndex, 1);

  return of(
    new HttpResponse({
      status: 204,
    })
  ).pipe(delay(300));
}
